/**
 * SelfScreenPanel — TV-P5-09, migrated to @collabkare/sdc-react (SDC-INT-2).
 *
 * Patient-side mount for a self-complete screening Questionnaire (default:
 * PRAPARE) while they wait. Results land as a real FHIR
 * QuestionnaireResponse + a derived total-score Observation (for scored
 * instruments) the provider sees on chart open.
 *
 * `questionnaireId` accepts either a bundled @collabkare/sdc-catalog id
 * (e.g. 'prapare') or a legacy HAPI-hosted id (e.g. 'prapare-loinc') — the
 * legacy ids are mapped to their catalog equivalent so callers don't need
 * to change. Anything not in the map falls back to loading the FHIR
 * Questionnaire from the server, same as before.
 *
 * Why this lives separately from IntakeChecklist:
 *   - The checklist's "questionnaire" step is a free-text chief
 *     complaint that gates the provider-visible queue status; we
 *     don't want PRAPARE completion to be a hard requirement
 *     before the visit. Keeping the screener as an additive panel
 *     means a patient who skips it still progresses cleanly.
 *   - PRAPARE is patient-authored; SdcForm's buildQr always stamps
 *     `status: 'completed'` on submit — authorship of the Encounter/
 *     patient linkage is handled the same way as the provider-side
 *     Assessment tab via useSaveQuestionnaireResponse.
 *
 * Skipped (panel hidden) when:
 *   - No FHIR patientId in session (e.g. KC token without patient
 *     claim — banner from PatientWaitingRoom already covers that).
 */

import React, { useMemo, useState } from 'react';
import { Card } from '@/components';
import { ClipboardCheck, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  usePatient,
  useSaveQuestionnaireResponse,
  useQuestionnaire,
} from '@/fhir/hooks';
import { extractObservations } from '@collabkare/sdc';
import { catalogInstruments } from '@collabkare/sdc-catalog';
import { SdcForm } from '@collabkare/sdc-react';

// Legacy HAPI-hosted questionnaire ids → bundled catalog instrument id.
const LEGACY_ID_MAP = {
  'prapare-loinc': 'prapare',
  'phq-9-loinc': 'phq-9',
  'gad-7-loinc': 'gad-7',
  'audit-c-loinc': 'audit-c',
};

// Known LOINC total-score codes for scored catalog instruments (mirrors
// Assessment.jsx's tab config — the two mounts intentionally agree).
const SCORE_CODES = {
  'phq-9': { system: 'http://loinc.org', code: '44261-6', display: 'Patient Health Questionnaire 9 item (PHQ-9) total score' },
  'gad-7': { system: 'http://loinc.org', code: '70274-6', display: 'Generalized anxiety disorder 7 item (GAD-7) total score' },
  'audit-c': { system: 'http://loinc.org', code: '75624-7', display: 'AUDIT-C Total score' },
};

const catalogById = new Map(catalogInstruments.map((i) => [i.id, i]));

export default function SelfScreenPanel({ patientId, questionnaireId }) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const catalogId = LEGACY_ID_MAP[questionnaireId] || questionnaireId;
  const instrument = catalogById.get(catalogId);

  const { data: patientResource } = usePatient(patientId);
  // Only hit the FHIR server for Questionnaires with no bundled catalog match.
  const { data: serverQuestionnaire } = useQuestionnaire(instrument ? undefined : questionnaireId);
  const questionnaire = instrument ? instrument.questionnaire : serverQuestionnaire;

  const saveMutation = useSaveQuestionnaireResponse();

  const launchContext = useMemo(() => {
    if (!patientId) return undefined;
    return {
      patient: patientResource
        ? { resourceType: 'Patient', id: patientId, ...patientResource }
        : `Patient/${patientId}`,
    };
  }, [patientId, patientResource]);

  if (!patientId) return null;

  const handleSubmit = async (qr) => {
    setSaveError(null);
    try {
      const observations = questionnaire
        ? extractObservations(questionnaire, qr, {
            subject: { reference: `Patient/${patientId}` },
            // No encounter yet — waiting room is pre-visit.
            ...(SCORE_CODES[catalogId] ? { scoreCode: SCORE_CODES[catalogId] } : {}),
          })
        : [];
      const result = await saveMutation.mutateAsync({ qr, observations });
      setSaved({
        qrId: result.qr?.id || null,
        observationCount: result.observations?.length || 0,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SelfScreenPanel] save failed:', err);
      setSaveError(err);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/40 rounded-lg"
      >
        <div className="flex items-center gap-3 text-left">
          <ClipboardCheck className="w-5 h-5 text-blue-400" />
          <div>
            <p className="font-semibold text-white text-sm">
              {questionnaire?.title || 'While you wait — screening'}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Optional. About 2 minutes. Results go straight to your provider.
            </p>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="p-4 border-t border-slate-800 space-y-3">
          {saved ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-200">
                <p className="font-semibold">Thanks! Your responses are saved.</p>
                <p className="text-xs text-emerald-200/80 mt-1">
                  Your provider will see these when the visit starts.
                </p>
              </div>
            </div>
          ) : (
            <>
              {saveError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-200">
                    {saveError.message || "Couldn't save your responses. Try again."}
                  </p>
                </div>
              )}
              {questionnaire && (
                <div className="rounded-lg bg-white p-4">
                  <SdcForm
                    key={formKey}
                    questionnaire={questionnaire}
                    instrument={instrument}
                    launchContext={launchContext}
                    density="comfortable"
                    onSubmit={(qr) => {
                      handleSubmit(qr);
                      setFormKey((k) => k + 1);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
